import { injectable } from 'inversify';
import { Collection, Db, getMongoDb } from "@swoft/persistence";
import { NextAction } from '../../domain/aggregates/NextAction';
import { ActionContext, EnergyLevel } from '../../domain/value-objects';

/**
 * MongoDB Repository for NextAction Aggregate
 * 
 * Handles persistence and retrieval of GTD next actions
 */
@injectable()
export class NextActionAggregateRepository {
  private db: Db | null = null;
  private collection: Collection | null = null;

  constructor() {
    // Lazy initialization - don't connect until needed
  }

  private ensureConnection() {
    if (!this.db) {
      this.db = getMongoDb();
      this.collection = this.db.collection('gtd_next_actions');
    }
    return this.collection!;
  }

  async save(action: NextAction): Promise<void> {
    const collection = this.ensureConnection();
    const doc = {
      _id: action.id,
      description: action.description,
      context: action.context.toString(),
      energyRequired: action.energyRequired.toString(),
      estimatedMinutes: action.estimatedMinutes,
      createdBy: action.createdBy,
      createdAt: action.createdAt,
      projectId: action.projectId,
      assignedTo: action.assignedTo,
      assignedAt: action.assignedAt,
      completedAt: action.completedAt,
      status: action.isCompleted() ? 'completed' : action.isAssigned() ? 'assigned' : 'available'
    };

    await collection.replaceOne(
      { _id: action.id as any },
      doc,
      { upsert: true }
    );
  }

  async findById(id: string): Promise<NextAction | null> {
    const collection = this.ensureConnection();
    const doc = await collection.findOne({ _id: id as any });
    if (!doc) return null;

    // Reconstruct domain object from document
    const context = this.parseContext(doc.context);
    const energy = this.parseEnergyLevel(doc.energyRequired);
    
    return NextAction.reconstitute(
      doc._id.toString(),
      doc.description,
      context,
      energy,
      doc.estimatedMinutes,
      doc.createdBy,
      doc.createdAt,
      doc.projectId,
      doc.assignedTo,
      doc.assignedAt,
      doc.roleType,
      doc.completedAt
    );
  }

  async findAll(): Promise<NextAction[]> {
    const collection = this.ensureConnection();
    const docs = await collection.find({}).toArray();
    return docs.map(doc => {
      const context = this.parseContext(doc.context);
      const energy = this.parseEnergyLevel(doc.energyRequired);
      
      return NextAction.reconstitute(
        doc._id.toString(),
        doc.description,
        context,
        energy,
        doc.estimatedMinutes,
        doc.createdBy,
        doc.createdAt,
        doc.projectId,
        doc.assignedTo,
        doc.assignedAt,
        doc.roleType,
        doc.completedAt
      );
    });
  }

  async countAvailable(): Promise<number> {
    const collection = this.ensureConnection();
    return await collection.countDocuments({
      status: 'available'
    });
  }

  async countAll(): Promise<number> {
    const collection = this.ensureConnection();
    return await collection.countDocuments({});
  }

  async delete(id: string): Promise<void> {
    const collection = this.ensureConnection();
    await collection.deleteOne({ _id: id as any });
  }

  // Helper methods
  private parseContext(contextStr: string): ActionContext {
    switch (contextStr) {
      case '@calls': return ActionContext.atCalls();
      case '@computer': return ActionContext.atComputer();
      case '@errands': return ActionContext.atErrands('');
      case '@home': return ActionContext.atHome();
      case '@office': return ActionContext.atOffice();
      case '@anywhere': return ActionContext.anywhere();
      default: return ActionContext.atComputer();
    }
  }

  private parseEnergyLevel(energyStr: string): EnergyLevel {
    switch (energyStr) {
      case 'high': return EnergyLevel.high();
      case 'medium': return EnergyLevel.medium();
      case 'low': return EnergyLevel.low();
      default: return EnergyLevel.medium();
    }
  }
}
